import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Save, Plus, X, ArrowUp, ArrowDown, EyeOff, Eye, Upload, GripVertical, Image as ImageIcon } from "lucide-react";
import { StockPicker } from "../../components/StockPicker";
import { BLOCK_DEFS, BlockRenderer, ICON_NAMES } from "../../components/Blocks";

const empty = {
  slug: "", title: "", blocks: [], in_menu: false, menu_order: 100, published: true,
  meta_title: "", meta_description: "", og_image: "",
};

export default function PageForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [previewWidth, setPreviewWidth] = useState("100%");
  // Drag-and-drop state
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  // Stable UID counter for blocks (survives reorder/hide/delete)
  const blockUidCounter = useRef(0);
  const nextUid = () => `b${++blockUidCounter.current}`;

  const handleDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    // Make Firefox happy
    e.dataTransfer.setData("text/plain", String(i));
  };
  const handleDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverIdx !== i) setDragOverIdx(i);
  };
  const handleDrop = (i) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null);
    setDragOverIdx(null);
    if (from === null || from === i) return;
    const next = [...form.blocks];
    const [moved] = next.splice(from, 1);
    next.splice(i, 0, moved);
    set("blocks", next);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    api.get("/admin/pages").then(r => {
      if (cancelled) return;
      const p = r.data.find(x => x.id === id);
      if (p) {
        const blocks = (p.blocks || []).map(b => ({
          ...b,
          _uid: b._uid || `b${++blockUidCounter.current}`,
        }));
        setForm({ ...empty, ...p, blocks });
      }
    }).catch(err => console.error("Failed to load page:", err));
    return () => { cancelled = true; };
  }, [id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");

  const addBlock = (type) => {
    set("blocks", [...form.blocks, { _uid: nextUid(), type, props: { ...(BLOCK_DEFS[type]?.defaults || {}) }, hidden: false }]);
  };
  const updateBlock = (i, key, value) => {
    const next = form.blocks.map((b, idx) => idx === i ? { ...b, props: { ...b.props, [key]: value } } : b);
    set("blocks", next);
  };
  const moveBlock = (i, dir) => {
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= form.blocks.length) return;
    const next = [...form.blocks];
    [next[i], next[j]] = [next[j], next[i]];
    set("blocks", next);
  };
  const toggleHidden = (i) => {
    set("blocks", form.blocks.map((b, idx) => idx === i ? { ...b, hidden: !b.hidden } : b));
  };
  const removeBlock = (i) => set("blocks", form.blocks.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const cleanBlocks = (form.blocks || []).map(b => { const { _uid, ...rest } = b; return rest; });
      const payload = { ...form, blocks: cleanBlocks, slug: form.slug || slugify(form.title) };
      if (id) {
        await api.put(`/admin/pages/${id}`, payload);
        toast.success("Page mise à jour");
      } else {
        const { data } = await api.post("/admin/pages", payload);
        toast.success("Page créée");
        navigate(`/admin/pages/${data.id}/edit`);
        return;
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin/pages")} className="text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-6 flex items-center gap-2"><ArrowLeft className="w-3 h-3"/> Pages</button>

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{id ? "Modifier la page" : "Nouvelle page"}</h1>
          <div className="flex gap-2">
            <button onClick={() => setPreview(!preview)} className="btn-brutal bg-white border border-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2">{preview ? <EyeOff className="w-3 h-3"/> : <Eye className="w-3 h-3"/>} {preview ? "Édition" : "Aperçu"}</button>
            <button onClick={submit} disabled={saving} data-testid="save-page" className="btn-brutal btn-brutal-solar px-5 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2">{saving ? "..." : "Enregistrer"}{!saving && <Save className="w-3 h-3"/>}</button>
          </div>
        </div>

        {preview ? (
          <div className="bg-white border border-slate-900/15">
            <div className="border-b border-slate-900/15 p-3 flex items-center gap-2 bg-slate-50">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Aperçu :</span>
              {[
                { key: "desktop", label: "Desktop", w: "100%" },
                { key: "tablet", label: "Tablette", w: "768px" },
                { key: "mobile", label: "Mobile", w: "375px" },
              ].map(v => (
                <button key={v.key} onClick={() => setPreviewWidth(v.w)} className={`text-xs px-3 py-1 border border-slate-900 font-semibold ${previewWidth === v.w ? "bg-slate-900 text-white" : ""}`}>{v.label}</button>
              ))}
            </div>
            <div className="overflow-x-auto bg-slate-100 p-4 flex justify-center">
              <div style={{ width: previewWidth, maxWidth: "100%" }} className="bg-white border border-slate-300">
                {(form.blocks || []).map((b, i) => <BlockRenderer key={b._uid || `prev-${i}`} block={b} />)}
                {form.blocks.length === 0 && <div className="p-16 text-center text-slate-500 font-mono">Aucun bloc.</div>}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Metadata */}
            <div className="bg-white border border-slate-900/15 p-6 md:p-8 mb-6 space-y-4">
              <h2 className="font-display font-bold text-lg mb-2">Paramètres de la page</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Titre *"><input required value={form.title} onChange={e => set("title", e.target.value)} className="input" data-testid="page-title" /></Field>
                <Field label="Slug (URL : /p/...)"><input value={form.slug} onChange={e => set("slug", e.target.value)} placeholder={slugify(form.title)} className="input font-mono" data-testid="page-slug" /></Field>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} className="w-4 h-4 accent-slate-900" data-testid="page-published"/><span className="text-sm font-semibold">Publiée</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.in_menu} onChange={e => set("in_menu", e.target.checked)} className="w-4 h-4 accent-slate-900" data-testid="page-in-menu"/><span className="text-sm font-semibold">Dans le menu</span></label>
                <Field label="Ordre menu"><input type="number" value={form.menu_order} onChange={e => set("menu_order", parseInt(e.target.value)||100)} className="input" /></Field>
              </div>
              <div className="border-t border-slate-900/10 pt-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">SEO</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Meta title"><input value={form.meta_title} onChange={e => set("meta_title", e.target.value)} className="input" /></Field>
                  <Field label="OG image (URL)"><input value={form.og_image} onChange={e => set("og_image", e.target.value)} className="input" /></Field>
                </div>
                <Field label="Meta description"><textarea rows={2} value={form.meta_description} onChange={e => set("meta_description", e.target.value)} className="input resize-none mt-3" /></Field>
              </div>
            </div>

            {/* Blocks */}
            <div className="bg-white border border-slate-900/15 p-6 md:p-8">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="font-display font-bold text-lg">Blocs ({form.blocks.length})</h2>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(BLOCK_DEFS).map(([key, def]) => (
                    <button key={key} onClick={() => addBlock(key)} data-testid={`add-block-${key}`} className="text-xs font-bold uppercase tracking-wider border border-slate-900 px-3 py-1.5 hover:bg-[#FFC107]"><Plus className="w-3 h-3 inline mr-1"/>{def.label}</button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {form.blocks.map((b, i) => (
                  <div
                    key={b._uid || `block-${i}`}
                    draggable
                    onDragStart={handleDragStart(i)}
                    onDragOver={handleDragOver(i)}
                    onDrop={handleDrop(i)}
                    onDragEnd={handleDragEnd}
                    className={`border ${b.hidden ? "border-dashed border-slate-400 bg-slate-50" : "border-slate-900/15 bg-[#F9F9F7]"} p-5 transition-all ${dragIdx === i ? "opacity-40" : ""} ${dragOverIdx === i && dragIdx !== null && dragIdx !== i ? "border-[#FFC107] border-2 border-dashed -translate-y-0.5" : ""}`}
                    data-testid={`block-${i}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700" title="Glisser pour réordonner" data-testid={`drag-handle-${i}`}>
                          <GripVertical className="w-4 h-4"/>
                        </span>
                        <span className="tag">{BLOCK_DEFS[b.type]?.label || b.type}</span>
                        {b.hidden && <span className="text-xs text-slate-500 italic">— masqué</span>}
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => moveBlock(i, "up")} className="p-1.5 border border-slate-900 hover:bg-slate-900 hover:text-white"><ArrowUp className="w-3 h-3"/></button>
                        <button onClick={() => moveBlock(i, "down")} className="p-1.5 border border-slate-900 hover:bg-slate-900 hover:text-white"><ArrowDown className="w-3 h-3"/></button>
                        <button onClick={() => toggleHidden(i)} className="p-1.5 border border-slate-900 hover:bg-slate-900 hover:text-white">{b.hidden ? <Eye className="w-3 h-3"/> : <EyeOff className="w-3 h-3"/>}</button>
                        <button onClick={() => removeBlock(i)} className="p-1.5 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600" data-testid={`del-block-${i}`}><X className="w-3 h-3"/></button>
                      </div>
                    </div>
                    <BlockFields block={b} onChange={(k, v) => updateBlock(i, k, v)} />
                  </div>
                ))}
                {form.blocks.length === 0 && <div className="p-10 text-center text-slate-500 text-sm font-mono">Aucun bloc — cliquez sur l'un des boutons ci-dessus pour commencer.</div>}
                {form.blocks.length > 1 && <div className="text-xs text-slate-400 font-mono text-center pt-2">💡 Astuce : glissez les blocs pour les réordonner, ou utilisez les flèches.</div>}
              </div>
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}

// Fields editor per block type
function BlockFields({ block, onChange }) {
  const p = block.props || {};
  const [stockOpenFor, setStockOpenFor] = useState(null); // image_url key or "_gallery"
  // IMPORTANT: T, TA, ImageControls are helper FUNCTIONS (not components rendered as <T ... />)
  // because defining a component inside another component creates a new component type on
  // every render, which causes React to unmount/remount the inputs on every keystroke,
  // making the field lose focus and the page jump/scroll.
  const T = (k, label, full) => (
    <label key={`t-${k}`} className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">{label}</span>
      <input value={p[k] || ""} onChange={e => onChange(k, e.target.value)} className="input" />
    </label>
  );
  const TA = (k, label) => (
    <label key={`ta-${k}`} className="block md:col-span-2">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">{label}</span>
      <textarea rows={4} value={p[k] || ""} onChange={e => onChange(k, e.target.value)} className="input resize-none" />
    </label>
  );
  const uploadImg = async (key) => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*";
    inp.onchange = async () => {
      const file = inp.files[0]; if (!file) return;
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        onChange(key, `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
        toast.success("Image uploadée");
      } catch { toast.error("Échec upload"); }
    };
    inp.click();
  };
  const ImageControls = (k, label = "Image (URL)") => (
    <div key={`ic-${k}`} className="md:col-span-2 flex gap-2 items-end flex-wrap">
      {T(k, label)}
      <button onClick={() => uploadImg(k)} type="button" className="btn-brutal bg-white border border-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1 mb-1" data-testid="upload-img-btn"><Upload className="w-3 h-3"/>Uploader</button>
      <button onClick={() => setStockOpenFor(k)} type="button" className="btn-brutal bg-[#FFC107] border border-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1 mb-1" data-testid="gallery-btn"><ImageIcon className="w-3 h-3"/>Galerie d'images libres</button>
      {p[k] && <a href={p[k]} target="_blank" rel="noreferrer" className="border border-slate-900 px-3 py-2 text-xs mb-1" title="Aperçu"><Eye className="w-3 h-3"/></a>}
    </div>
  );
  const renderStockModal = () => stockOpenFor && (
    <StockPicker
      onClose={() => setStockOpenFor(null)}
      onSelect={(url) => {
        if (stockOpenFor === "_gallery") {
          onChange("images", [...(p.images || []), { url, alt: "" }]);
          toast.success("Image ajoutée");
        } else {
          onChange(stockOpenFor, url);
          toast.success("Image sélectionnée");
        }
      }}
    />
  );

  if (block.type === "hero") return (
    <div className="grid md:grid-cols-2 gap-3">
      {T("kicker", "Kicker")}{T("title", "Titre *")}
      {TA("subtitle", "Sous-titre")}
      {T("cta_label", "Bouton — libellé")}{T("cta_link", "Bouton — lien")}
      {ImageControls("image_url", "Image de fond (URL)")}
      {renderStockModal()}
    </div>
  );
  if (block.type === "text") return (<div className="grid md:grid-cols-2 gap-3">{T("kicker", "Kicker")}{T("title", "Titre")}{TA("content", "Contenu")}</div>);
  if (block.type === "image_text") return (
    <div className="grid md:grid-cols-2 gap-3">
      {T("title", "Titre")}
      <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Position image</span>
        <select value={p.image_side || "right"} onChange={e => onChange("image_side", e.target.value)} className="input"><option value="left">Gauche</option><option value="right">Droite</option></select></label>
      {TA("content", "Contenu")}
      {ImageControls("image_url")}
      {renderStockModal()}
    </div>
  );
  if (block.type === "gallery") return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        {T("title", "Titre")}
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Nombre de colonnes (desktop)</span>
          <select value={p.layout || 4} onChange={e => onChange("layout", parseInt(e.target.value))} className="input">
            <option value={2}>2 colonnes</option><option value={3}>3 colonnes</option><option value={4}>4 colonnes</option><option value={5}>5 colonnes</option>
          </select>
        </label>
      </div>
      <ImageListEditor images={p.images || []} onChange={v => onChange("images", v)} uploadImg={uploadImg} onPickStock={() => setStockOpenFor("_gallery")} />
      {renderStockModal()}
    </div>
  );
  if (block.type === "video") return (<div className="grid md:grid-cols-2 gap-3">{T("title", "Titre")}{T("video_url", "URL vidéo (YouTube/Vimeo/upload)", true)}</div>);
  if (block.type === "cta") return (
    <div className="grid md:grid-cols-2 gap-3">
      {T("title", "Titre")}{T("subtitle", "Sous-titre")}
      {T("cta_label", "Bouton — libellé")}{T("cta_link", "Bouton — lien")}
    </div>
  );
  if (block.type === "faq") return (<ListEditor items={p.items || []} fields={[{ k: "q", label: "Question" }, { k: "a", label: "Réponse", multi: true }]} onChange={v => onChange("items", v)} title={T("title", "Titre")} />);
  if (block.type === "features") return (<ListEditor items={p.items || []} fields={[{ k: "title", label: "Titre" }, { k: "desc", label: "Description" }]} onChange={v => onChange("items", v)} title={T("title", "Titre")} />);
  if (block.type === "stats") return (<ListEditor items={p.items || []} fields={[{ k: "value", label: "Valeur" }, { k: "label", label: "Label" }]} onChange={v => onChange("items", v)} title={null} />);
  if (block.type === "testimonials" || block.type === "partners") return (
    <div>
      {T("title", "Titre")}
      <div className="text-xs text-slate-500 mt-2">Les éléments sont gérés depuis le menu "{block.type === "testimonials" ? "Témoignages" : "Partenaires"}".</div>
    </div>
  );
  if (block.type === "hero_full") return (
    <div className="grid md:grid-cols-2 gap-3">
      {T("title", "Titre *")}
      {TA("subtitle", "Sous-titre")}
      {ImageControls("image_url")}
      {renderStockModal()}
    </div>
  );
  if (block.type === "columns") return (
    <div>
      <div className="grid md:grid-cols-2 gap-3">
        {T("title", "Titre")}
        <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Nombre de colonnes</span>
          <select value={p.count || 3} onChange={e => onChange("count", parseInt(e.target.value))} className="input"><option value={2}>2 colonnes</option><option value={3}>3 colonnes</option><option value={4}>4 colonnes</option></select>
        </label>
      </div>
      <div className="mt-3">
        <ListEditor items={p.items || []} fields={[{ k: "title", label: "Titre" }, { k: "content", label: "Contenu", multi: true }]} onChange={v => onChange("items", v)} title={null} />
      </div>
    </div>
  );
  if (block.type === "quote") return (<div className="grid md:grid-cols-1 gap-3">{TA("content", "Citation *")}{T("author", "Auteur")}</div>);
  if (block.type === "counters") return (<ListEditor items={p.items || []} fields={[{ k: "value", label: "Valeur" }, { k: "suffix", label: "Suffixe (+, %, ans…)" }, { k: "label", label: "Label" }]} onChange={v => onChange("items", v)} title={null} />);
  if (block.type === "map") return (<div className="grid md:grid-cols-1 gap-3">{T("title", "Titre")}{T("embed_url", "URL Google Maps embed (src de l'iframe)")}</div>);

  // ----- Phase 9.2 new block editors -----
  if (block.type === "recent_properties") return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        {T("kicker", "Kicker")}{T("title", "Titre")}
        {TA("subtitle", "Sous-titre")}
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Nombre de cartes</span>
          <select value={p.count || 6} onChange={e => onChange("count", parseInt(e.target.value))} className="input">
            {[3, 4, 6, 8, 12].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Catégorie</span>
          <select value={p.category || "all"} onChange={e => onChange("category", e.target.value)} className="input">
            <option value="all">Toutes catégories</option>
            <option value="industriel">Industriel</option>
            <option value="solaire">Solaire</option>
            <option value="mixte">Mixte</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Affichage</span>
          <select value={p.display || "grid"} onChange={e => onChange("display", e.target.value)} className="input">
            <option value="grid">Grille</option><option value="slider">Slider horizontal</option>
          </select>
        </label>
      </div>
      <div className="grid md:grid-cols-3 gap-3 items-end">
        <label className="flex items-center gap-2 text-sm pt-5">
          <input type="checkbox" checked={p.show_cta !== false} onChange={e => onChange("show_cta", e.target.checked)} className="w-4 h-4 accent-slate-900"/>
          Afficher le bouton « Voir tous »
        </label>
        {T("cta_label", "Bouton — libellé")}
        {T("cta_link", "Bouton — lien")}
      </div>
    </div>
  );
  if (block.type === "territories") return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-3">
        {T("kicker", "Kicker")}{T("title", "Titre")}
        {TA("subtitle", "Sous-titre")}
      </div>
      <IconListEditor items={p.items || []} onChange={v => onChange("items", v)} fields={[
        { k: "name", label: "Ville/Département" },
        { k: "desc", label: "Description", multi: true },
        { k: "link", label: "Lien (/locaux, /contact…)" },
      ]} />
    </div>
  );
  if (block.type === "why_us") return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-3 gap-3">
        {T("kicker", "Kicker")}{T("title", "Titre")}
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Colonnes</span>
          <select value={p.count || 4} onChange={e => onChange("count", parseInt(e.target.value))} className="input">
            {[2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
      </div>
      <IconListEditor items={p.items || []} onChange={v => onChange("items", v)} fields={[
        { k: "title", label: "Titre" },
        { k: "desc", label: "Description", multi: true },
      ]} />
    </div>
  );
  return null;
}

// Visual image list editor (upload, reorder, delete)
function ImageListEditor({ images, onChange, uploadImg, onPickStock }) {
  const add = async () => {
    const inp = document.createElement("input");
    inp.type = "file"; inp.accept = "image/*"; inp.multiple = true;
    inp.onchange = async () => {
      const files = Array.from(inp.files || []);
      const next = [...images];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        try {
          const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          next.push({ url: `${process.env.REACT_APP_BACKEND_URL}${data.url}`, alt: "" });
        } catch { toast.error(`Échec upload ${file.name}`); }
      }
      onChange(next);
      toast.success(`${files.length} image(s) ajoutée(s)`);
    };
    inp.click();
  };
  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const ni = i + dir; if (ni < 0 || ni >= images.length) return;
    const next = [...images]; [next[i], next[ni]] = [next[ni], next[i]]; onChange(next);
  };
  const updateAlt = (i, alt) => onChange(images.map((img, idx) => idx === i ? { ...(typeof img === "string" ? { url: img } : img), alt } : img));
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">{images.length} image(s)</span>
        <div className="flex gap-2">
          {onPickStock && <button type="button" onClick={onPickStock} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-[#FFC107] flex items-center gap-1"><ImageIcon className="w-3 h-3"/>Galerie</button>}
          <button type="button" onClick={add} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-[#FFC107] flex items-center gap-1"><Upload className="w-3 h-3"/>Uploader</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {images.map((img, i) => {
          const url = typeof img === "string" ? img : img.url;
          const alt = typeof img === "object" ? (img.alt || "") : "";
          return (
            <div key={url || `img-${i}`} className="relative aspect-square border border-slate-900/20 group">
              <img src={url} alt={alt} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 p-2">
                <div className="flex gap-1">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="bg-white p-1 disabled:opacity-30"><ArrowUp className="w-3 h-3"/></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} className="bg-white p-1 disabled:opacity-30"><ArrowDown className="w-3 h-3"/></button>
                  <button type="button" onClick={() => remove(i)} className="bg-red-600 text-white p-1"><X className="w-3 h-3"/></button>
                </div>
                <input value={alt} onChange={e => updateAlt(i, e.target.value)} placeholder="Texte alt SEO" className="w-full text-[10px] px-2 py-1 mt-1"/>
              </div>
            </div>
          );
        })}
      </div>
      {images.length === 0 && <div className="border border-dashed border-slate-300 p-8 text-center text-xs text-slate-500 font-mono">Aucune image — cliquez sur « Ajouter des images »</div>}
    </div>
  );
}

// List editor with icon picker (for why_us, territories)
function IconListEditor({ items, fields, onChange }) {
  const update = (i, k, v) => onChange(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const add = () => onChange([...items, { icon: ICON_NAMES[0], ...fields.reduce((a, f) => ({ ...a, [f.k]: "" }), {}) }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={`il-${i}`} className="border border-slate-900/15 p-3 bg-white space-y-2">
            <div className="flex gap-2 items-start">
              <label className="block w-32 shrink-0">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">Icône</span>
                <select value={it.icon || ICON_NAMES[0]} onChange={e => update(i, "icon", e.target.value)} className="input text-xs">
                  {ICON_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                {fields.map(f => (
                  f.multi
                    ? <textarea key={f.k} rows={2} value={it[f.k] || ""} onChange={e => update(i, f.k, e.target.value)} placeholder={f.label} className="input resize-none md:col-span-2 text-sm" />
                    : <input key={f.k} value={it[f.k] || ""} onChange={e => update(i, f.k, e.target.value)} placeholder={f.label} className="input text-sm" />
                ))}
              </div>
              <button type="button" onClick={() => remove(i)} className="text-red-600 mt-5"><X className="w-4 h-4"/></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={add} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white flex items-center gap-1"><Plus className="w-3 h-3"/>Ajouter</button>
      </div>
    </div>
  );
}

function ListEditor({ items, fields, onChange, title }) {
  const update = (i, k, v) => onChange(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const add = () => onChange([...items, fields.reduce((acc, f) => ({ ...acc, [f.k]: "" }), {})]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      {title}
      <div className="mt-3 space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid md:grid-cols-[1fr_2fr_auto] gap-2 items-start border border-slate-900/15 p-3 bg-white">
            {fields.map((f) => (
              f.multi
                ? <textarea key={f.k} rows={2} value={it[f.k] || ""} onChange={e => update(i, f.k, e.target.value)} placeholder={f.label} className="input resize-none md:col-span-2" />
                : <input key={f.k} value={it[f.k] || ""} onChange={e => update(i, f.k, e.target.value)} placeholder={f.label} className="input" />
            ))}
            <button type="button" onClick={() => remove(i)} className="text-red-600 mt-2"><X className="w-4 h-4"/></button>
          </div>
        ))}
        <button type="button" onClick={add} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white flex items-center gap-1"><Plus className="w-3 h-3"/>Ajouter</button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">{label}</span>{children}</label>
);
